<template>
  <div class="resume-view-container">
    <!-- 顶部操作栏：返回/打印等 -->
    <div class="action-bar">
      <div class="title-tip">
        <i class="el-icon-document header-icon" />
        <span class="title-text">个人简历预览</span>
      </div>
      <div class="header-action">
        <el-button
          type="primary"
          icon="el-icon-printer"
          size="small"
          @click="handlePrint"
        >
          打印 / 导出 A4 PDF
        </el-button>
      </div>
    </div>

    <!-- A4 简历纸张主体容器 -->
    <div id="printResumeArea" class="resume-paper-wrapper">
      <!-- 头部：左侧姓名与求职意向（上下结构），右侧二维码 -->
      <div class="preview-top-banner">
        <div class="preview-banner-left">
          <h2 class="preview-person-name">{{ form.name || '姓名未填写' }}</h2>
          <div v-if="form.targetPosition" class="preview-target-job">
            <i class="el-icon-position" /> {{ form.targetPosition }}
          </div>
        </div>
        <div v-if="form.qrcodeUrl" class="preview-banner-right">
          <img :src="form.qrcodeUrl" alt="微信二维码" class="preview-qrcode">
          <span class="qrcode-tip">扫我加微信</span>
        </div>
      </div>

      <el-divider />

      <!-- 基本信息 -->
      <div class="section-title"><i class="el-icon-s-custom" /> 基本信息</div>
      <div class="preview-info-grid">
        <div class="preview-info-item"><strong>姓名：</strong><span>{{ form.name || '-' }}</span></div>
        <div class="preview-info-item"><strong>求职意向：</strong><span>{{ form.targetPosition || '-' }}</span></div>
        <div class="preview-info-item"><strong>手机号：</strong><span>{{ form.phone || '-' }}</span></div>
        <div class="preview-info-item"><strong>电子邮箱：</strong><span>{{ form.email || '-' }}</span></div>
        <div class="preview-info-item"><strong>年龄：</strong><span>{{ form.age || '-' }}</span></div>
        <div class="preview-info-item"><strong>工作年限：</strong><span>{{ form.workYears || '-' }}</span></div>
        <div class="preview-info-item"><strong>个人博客：</strong><span>{{ form.blog || '-' }}</span></div>
        <div class="preview-info-item"><strong>代码仓库：</strong><span>{{ form.github || '-' }}</span></div>
      </div>

      <el-divider />

      <!-- 教育背景 -->
      <div class="section-title"><i class="el-icon-reading" /> 教育背景</div>
      <div v-for="(edu, eIdx) in form.educationList" :key="'p-edu-' + eIdx" class="preview-sub-item">
        <div class="preview-item-row">
          <strong>{{ edu.school || '学校名称' }}</strong>
          <span>{{ edu.major || '专业' }} | {{ edu.degree || '学历' }}</span>
          <span class="text-muted">{{ formatTimeRange(edu.timeRange) }}</span>
        </div>
      </div>
      <el-empty v-if="!form.educationList || form.educationList.length === 0" description="暂无教育背景" :image-size="40" />

      <el-divider />

      <!-- 技能特长 -->
      <div class="section-title"><i class="el-icon-star-off" /> 技能特长</div>
      <p class="preview-text pre-wrap">{{ form.skills || '暂无填写' }}</p>

      <el-divider />

      <!-- 工作经历 -->
      <div class="section-title"><i class="el-icon-s-cooperation" /> 工作经历</div>
      <div v-for="(work, wIdx) in form.workList" :key="'p-work-' + wIdx" class="preview-sub-item">
        <div class="preview-item-row">
          <strong>{{ work.company || '公司名称' }}</strong>
          <span class="text-primary">{{ work.position || '职位' }}</span>
          <span class="text-muted">{{ formatTimeRange(work.timeRange) }}</span>
        </div>
        <p class="preview-text pre-wrap">{{ work.content || '暂无内容' }}</p>
      </div>
      <el-empty v-if="!form.workList || form.workList.length === 0" description="暂无工作经历" :image-size="40" />

      <el-divider />

      <!-- 项目经验 -->
      <div class="section-title"><i class="el-icon-s-opportunity" /> 项目经验</div>
      <div v-for="(proj, pIdx) in form.projectList" :key="'p-proj-' + pIdx" class="preview-sub-item">
        <div class="preview-item-row">
          <div>
            <strong>{{ proj.projectName || '项目名称' }}</strong>
            <span v-if="proj.company" class="company-tag">（{{ proj.company }}）</span>
          </div>
          <span class="text-primary">{{ proj.role || '角色' }}</span>
          <span class="text-muted">{{ formatTimeRange(proj.timeRange) }}</span>
        </div>
        <p class="preview-text pre-wrap">{{ proj.description || '暂无描述' }}</p>
      </div>
      <el-empty v-if="!form.projectList || form.projectList.length === 0" description="暂无项目经验" :image-size="40" />

      <el-divider />

      <!-- 个人项目 (已按指定格式更新) -->
      <div class="section-title"><i class="el-icon-share" /> 个人项目</div>
      <div v-for="(pp, ppIdx) in form.personalProjectList" :key="'p-pp-' + ppIdx" class="preview-project-card">
        <div class="preview-field-row">
          <span class="preview-field-label">项目名称：</span>
          <span class="preview-field-value"><strong>{{ pp.projectName || '未命名项目' }}</strong></span>
        </div>
        <div v-if="pp.codeRepo" class="preview-field-row">
          <span class="preview-field-label">项目地址：</span>
          <span class="preview-field-value">{{ pp.codeRepo }}</span>
        </div>
        <div v-if="pp.description" class="preview-field-row">
          <span class="preview-field-label">项目简介：</span>
          <span class="preview-field-value preview-text pre-wrap">{{ pp.description }}</span>
        </div>
      </div>
      <el-empty v-if="!form.personalProjectList || form.personalProjectList.length === 0" description="暂无个人项目" :image-size="40" />

      <el-divider />

      <!-- 自我评价 -->
      <div class="section-title"><i class="el-icon-user" /> 自我评价</div>
      <p class="preview-text pre-wrap">{{ form.selfEvaluation || '暂无填写' }}</p>
    </div>
  </div>
</template>

<script>
import { getResume } from '@/api/account'

export default {
  name: 'ResumeView',
  data() {
    return {
      form: {
        name: '',
        targetPosition: '',
        phone: '',
        email: '',
        age: '',
        workYears: '',
        blog: '',
        github: '',
        skills: '',
        selfEvaluation: '',
        qrcodeUrl: '',
        educationList: [],
        workList: [],
        projectList: [],
        personalProjectList: []
      }
    }
  },
  created() {
    document.title = '个人简历'
    this.getData()
  },
  methods: {
    getData() {
      getResume().then(resp => {
        if (resp.code === 0 && resp.data) {
          this.form = typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data
        } else if (resp.code !== 0 && resp.code !== undefined) {
          this.$message.error(resp.msg || '获取简历数据失败')
        }
      }).catch(error => {
        this.$message.error(error.message || '网络异常')
      })
    },
    formatTimeRange(range) {
      if (!range || range.length === 0) return ''
      return `${range[0]} 至 ${range[1] || '至今'}`
    },
    handlePrint() {
      window.print()
    }
  }
}
</script>

<style scoped>
.resume-view-container {
  padding: 24px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.action-bar {
  max-width: 900px;
  margin: 0 auto 16px auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 12px 20px;
  border-radius: 8px;
  border: 1px solid #e6ebf5;
}

.title-tip {
  display: flex;
  align-items: center;
}

.header-icon {
  font-size: 18px;
  color: #409eff;
  margin-right: 6px;
}

.title-text {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

/* A4 简历纸张风格容器 */
.resume-paper-wrapper {
  max-width: 900px;
  margin: 0 auto;
  background-color: #fff;
  padding: 32px 40px;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
  color: #333;
  box-sizing: border-box;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 16px 0 12px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.text-danger {
  color: #f56c6c;
}

.text-primary {
  color: #409eff;
}

.text-muted {
  color: #909399;
  font-size: 12px;
}

.company-tag {
  font-size: 12px;
  color: #606266;
  font-weight: normal;
  margin-left: 2px;
}

/* ==================== 顶部横幅（左侧上下结构 + 右侧二维码） ==================== */
.preview-top-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 4px;
}

.preview-banner-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-person-name {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  color: #1f2937;
}

.preview-target-job {
  font-size: 14px;
  font-weight: 600;
  color: #409eff;
  background-color: #ecf5ff;
  border: 1px solid #d9ecff;
  padding: 4px 12px;
  border-radius: 12px;
  display: inline-block;
  width: fit-content;
}

.preview-banner-right {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qrcode-tip {
  font-size: 11px;
  color: #909399;
  letter-spacing: 0.5px;
  font-weight: 500;
  text-align: center;
}

.preview-qrcode {
  width: 105px;
  height: 105px;
  object-fit: cover;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  padding: 4px;
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.preview-info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 20px;
  font-size: 13px;
  color: #606266;
}

.preview-sub-item {
  margin-bottom: 12px;
}

.preview-item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #303133;
  margin-bottom: 4px;
}

/* 个人项目卡片布局样式 */
.preview-project-card {
  background-color: #fafbfc;
  border: 1px solid #e4e7ed;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.preview-field-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
  font-size: 13px;
  color: #303133;
  line-height: 1.5;
}

.preview-field-row:last-child {
  margin-bottom: 0;
}

.preview-field-label {
  width: 75px;
  flex-shrink: 0;
  color: #909399;
  font-weight: 500;
  text-align: right;
  padding-right: 8px;
}

.preview-field-value {
  flex: 1;
  word-break: break-all;
}

.preview-text {
  font-size: 12.5px;
  color: #606266;
  line-height: 1.6;
  margin: 0;
}

.pre-wrap {
  white-space: pre-wrap;
}

/* ==================== A4 纸打印与 PDF 导出优化样式 ==================== */
@page {
  size: A4;
  margin: 10mm;
}

@media print {
  html, body, .resume-view-container {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    background: #fff !important;
  }

  body * {
    visibility: hidden;
  }

  .action-bar {
    display: none !important;
  }

  #printResumeArea,
  #printResumeArea * {
    visibility: visible;
  }

  #printResumeArea {
    position: static !important;
    width: 100% !important;
    max-width: none !important;
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    box-shadow: none !important;
    border: none !important;
    box-sizing: border-box !important;
  }

  .preview-sub-item,
  .preview-project-card,
  .section-title,
  .preview-info-grid {
    page-break-inside: avoid;
    break-inside: avoid;
  }
}
</style>
